import React from 'react';
import {
  Download, Eye, QrCode, CloudUpload,
  Grid, Undo2, Redo2,
  AlignCenter, ArrowUpDown, ArrowLeftRight,
} from 'lucide-react';

import { getPermissions } from '../utils/permissions';

interface GeneratorHeaderProps {
  userRole?: string;
  authUser?: any;
  moduleStatuses?: Record<string, string>;
  // grid/snap
  gridSnapping: boolean;
  authUserPerms?: Record<string, boolean>;
  setGridSnapping: (v: boolean) => void;
  showGridLines: boolean;
  setShowGridLines: (v: boolean) => void;

  // undo/redo
  historyPointer: number;
  historyLength: number;
  handleUndo: () => void;
  handleRedo: () => void;

  // align
  elements: any[];
  handleAutoAlign: (mode: 'horizontal-distribution' | 'vertical-distribution' | 'center-horizontal' | 'center-vertical') => void;

  // bilingual
  isTranslating: boolean;
  bilingualMode: boolean;
  setBilingualMode: (v: boolean) => void;
  bilingualDirection: 'en-ar' | 'ar-en';
  setBilingualDirection: (v: 'en-ar' | 'ar-en') => void;
  handleTranslateToArabic: () => void;
  handleRestackBilingual: () => void;
  translateTargetLang: string;
  setTranslateTargetLang: (v: string) => void;

  // actions
  handleGenerateShareLink: () => void;
 
  handleExportPDF: () => void;
  handleSaveActiveWorkspaceAsBill: () => void;
  onSaveAsTemplate: () => void;
  onOpenScanner: () => void;
  onResetCanvas: () => void;
  handleSeparatePage: () => void;
handleSplitLayout: () => void;
  setIsPrintPreviewOpen: (v: boolean) => void;
  setPrintSelectedElementId: (v: string | null) => void;
}

const GeneratorHeader: React.FC<GeneratorHeaderProps> = ({
  userRole, authUser, authUserPerms, moduleStatuses, 
  gridSnapping, setGridSnapping,
  showGridLines, setShowGridLines,
  historyPointer, historyLength,
  handleUndo, handleRedo,
  elements, handleAutoAlign,
  isTranslating, bilingualMode, setBilingualMode,
  bilingualDirection, setBilingualDirection,
  handleTranslateToArabic, handleRestackBilingual,
  translateTargetLang, setTranslateTargetLang,
  handleGenerateShareLink, handleExportPDF,
  handleSaveActiveWorkspaceAsBill,
  onSaveAsTemplate,
  onOpenScanner,
  onResetCanvas,
  handleSeparatePage,
  handleSplitLayout,
  setIsPrintPreviewOpen, setPrintSelectedElementId,
}) => {
  const perms = getPermissions(
    userRole,
    authUserPerms,
    undefined,
    undefined,
    undefined,
    null
  );

  const isMobileHeaderEmbed =
    new URLSearchParams(window.location.search).get('embed') ===
    'invoice-header';

  const runHeaderAction = (
    action: string,
    payload: Record<string, any> = {},
    webAction?: () => void,
  ) => {
    if (
      isMobileHeaderEmbed &&
      (window as any).ReactNativeWebView?.postMessage
    ) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          action: 'invoice-header-action',
          headerAction: action,
          ...payload,
        }),
      );

      return;
    }

    webAction?.();
  };

  return (
  <header 
    className="bg-white border-b border-slate-200 px-2 flex flex-col shrink-0 z-20"
    style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
  >
<style>{`
  .generator-header-scroll::-webkit-scrollbar { height: 4px; }
  .generator-header-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
  .generator-header-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
`}</style>
<div className="flex items-center gap-1 h-[46px] overflow-x-auto generator-header-scroll">

      {/* Brand */}
      <div className="flex items-center gap-2 mr-1 shrink-0">
        <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center text-white">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="9" y1="12" x2="15" y2="12"/>
            <line x1="9" y1="16" x2="13" y2="16"/>
          </svg>
        </div>
        <span className="text-sm font-semibold text-slate-900 hidden xl:inline">
  Invoice Studio <span className="text-slate-400 font-normal text-xs">/ Editor</span>
</span>
      </div>

      <div className="w-px h-5 bg-slate-200 shrink-0 mx-0.5" />

      {/* Snap / Grid toggles */}
      <div className="flex items-center gap-0.5 bg-slate-100 border border-slate-200 rounded-lg p-0.5">
        <button
          onClick={() =>
  runHeaderAction(
    'set-grid-snapping',
    { enabled: !gridSnapping },
    () => setGridSnapping(!gridSnapping),
  )
}
          className={`h-[26px] px-2.5 text-[11px] font-medium rounded-md flex items-center gap-1 transition-all ${gridSnapping ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Grid className="w-3 h-3" /> Snap
        </button>
        <button
          onClick={() =>
  runHeaderAction(
    'set-show-grid-lines',
    { enabled: !showGridLines },
    () => setShowGridLines(!showGridLines),
  )
}
          className={`h-[26px] px-2.5 text-[11px] font-medium rounded-md flex items-center gap-1 transition-all ${showGridLines ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Eye className="w-3 h-3" /> Grid
        </button>
      </div>

      <div className="w-px h-5 bg-slate-200 shrink-0 mx-0.5" />

      {/* Auto-align */}
      <div className="flex items-center">
        {[
          { label: 'Center H',     action: 'center-horizontal'      as const, icon: <AlignCenter className="w-3.5 h-3.5" /> },
          { label: 'Center V',     action: 'center-vertical'        as const, icon: <ArrowUpDown className="w-3.5 h-3.5" /> },
          { label: 'Distribute H', action: 'horizontal-distribution' as const, icon: <ArrowLeftRight className="w-3.5 h-3.5" /> },
          { label: 'Distribute V', action: 'vertical-distribution'  as const, icon: <ArrowUpDown className="w-3.5 h-3.5 rotate-90" /> },
        ].map((item, i, arr) => (
          <button
            key={item.action}
            onClick={() =>
  runHeaderAction(
    'auto-align',
    { mode: item.action },
    () => handleAutoAlign(item.action),
  )
}
            title={item.label}
            disabled={
  !isMobileHeaderEmbed &&
  item.action.includes('distribution') &&
  elements.length < 2
}
            className={`w-7 h-7 flex items-center justify-center border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors ${i === 0 ? 'rounded-l-md border-r-0' : i === arr.length - 1 ? 'rounded-r-md' : 'border-r-0'}`}
          >
            {item.icon}
          </button>
        ))}
      </div>

      <div className="w-px h-5 bg-slate-200 shrink-0 mx-0.5" />

      {/* Undo / Redo */}
      <div className="flex items-center">
        <button onClick={() =>
  runHeaderAction('undo', {}, handleUndo)
} disabled={!isMobileHeaderEmbed && historyPointer <= 0} title="Undo (Ctrl+Z)"
          className="w-7 h-7 flex items-center justify-center border border-slate-200 border-r-0 bg-white rounded-l-md text-slate-500 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <Undo2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() =>
  runHeaderAction('redo', {}, handleRedo)
} disabled={!isMobileHeaderEmbed && historyPointer >= historyLength - 1} title="Redo (Ctrl+Y)"
          className="w-7 h-7 flex items-center justify-center border border-slate-200 bg-white rounded-r-md text-slate-500 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <Redo2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="w-px h-5 bg-slate-200 shrink-0 mx-0.5" />

      {/* Arabic / Bilingual */}
      <div className="flex items-center gap-0 shrink-0">
        <select
          value={translateTargetLang}
          onChange={(e) => {
  const language = e.target.value;

  runHeaderAction(
    'set-translate-language',
    { language },
    () => setTranslateTargetLang(language),
  );
}}
          className="h-[26px] w-[80px] px-1 text-[10px] font-medium bg-white border border-slate-200 border-r-0 rounded-l-md text-slate-700 focus:outline-none cursor-pointer"
        >
          {[
            ['af','Afrikaans'],['sq','Albanian'],['am','Amharic'],['ar','Arabic'],['hy','Armenian'],
['az','Azerbaijani'],['eu','Basque'],['be','Belarusian'],['bn','Bengali'],['bs','Bosnian'],
['bg','Bulgarian'],['ca','Catalan'],['ceb','Cebuano'],['ny','Chichewa'],['zh','Chinese (Simplified)'],
['zh-TW','Chinese (Traditional)'],['co','Corsican'],['hr','Croatian'],['cs','Czech'],['da','Danish'],
['nl','Dutch'],['en','English'],['eo','Esperanto'],['et','Estonian'],['tl','Filipino'],
['fi','Finnish'],['fr','French'],['fy','Frisian'],['gl','Galician'],['ka','Georgian'],
['de','German'],['el','Greek'],['gu','Gujarati'],['ht','Haitian Creole'],['ha','Hausa'],
['haw','Hawaiian'],['iw','Hebrew'],['hi','Hindi'],['hmn','Hmong'],['hu','Hungarian'],
['is','Icelandic'],['ig','Igbo'],['id','Indonesian'],['ga','Irish'],['it','Italian'],
['ja','Japanese'],['jw','Javanese'],['kn','Kannada'],['kk','Kazakh'],['km','Khmer'],
['rw','Kinyarwanda'],['ko','Korean'],['ku','Kurdish (Kurmanji)'],['ky','Kyrgyz'],['lo','Lao'],
['la','Latin'],['lv','Latvian'],['lt','Lithuanian'],['lb','Luxembourgish'],['mk','Macedonian'],
['mg','Malagasy'],['ms','Malay'],['ml','Malayalam'],['mt','Maltese'],['mi','Maori'],
['mr','Marathi'],['mn','Mongolian'],['my','Myanmar (Burmese)'],['ne','Nepali'],['no','Norwegian'],
['or','Odia (Oriya)'],['ps','Pashto'],['fa','Persian'],['pl','Polish'],['pt','Portuguese'],
['pa','Punjabi'],['ro','Romanian'],['ru','Russian'],['sm','Samoan'],['gd','Scots Gaelic'],
['sr','Serbian'],['st','Sesotho'],['sn','Shona'],['sd','Sindhi'],['si','Sinhala'],
['sk','Slovak'],['sl','Slovenian'],['so','Somali'],['es','Spanish'],['su','Sundanese'],
['sw','Swahili'],['sv','Swedish'],['tg','Tajik'],['ta','Tamil'],['tt','Tatar'],
['te','Telugu'],['th','Thai'],['tr','Turkish'],['tk','Turkmen'],['ug','Uyghur'],
['uk','Ukrainian'],['ur','Urdu'],['uz','Uzbek'],['vi','Vietnamese'],['cy','Welsh'],
['xh','Xhosa'],['yi','Yiddish'],['yo','Yoruba'],['zu','Zulu'],['ak','Akan'],
['bho','Bhojpuri'],['doi','Dogri'],['dv','Dhivehi'],['ee','Ewe'],['gn','Guarani'],
['ilo','Ilocano'],['kri','Krio'],['ckb','Kurdish (Sorani)'],['lg','Luganda'],['mai','Maithili'],
['lus','Mizo'],['om','Oromo'],['qu','Quechua'],['sa','Sanskrit'],['nso','Sepedi'],
['ti','Tigrinya'],['ts','Tsonga'],
          ].map(([code, name]) => (
            <option key={code} value={code}>{code.toUpperCase()} — {name}</option>
          ))}
        </select>
        <button
          onClick={() =>
  runHeaderAction(
    'translate',
    { language: translateTargetLang },
    handleTranslateToArabic,
  )
}
          disabled={isTranslating}
          title={isTranslating ? 'Translating…' : 'Translate'}
          className="h-[26px] px-2 text-[10px] font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-r-md flex items-center gap-1 disabled:opacity-50 transition-colors"
        >
          <span className="text-xs">🌐</span>
          <span className="hidden 2xl:inline">{isTranslating ? '…' : 'Translate'}</span>
        </button>
      </div>

      <button
        onClick={() =>
  runHeaderAction(
    'set-bilingual-mode',
    { enabled: !bilingualMode },
    () => setBilingualMode(!bilingualMode),
  )
}
        className={`h-[26px] px-2 text-[10px] font-medium rounded-full flex items-center gap-1 border transition-all shrink-0 ${
          bilingualMode ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-transparent text-slate-500 border-slate-200 hover:bg-slate-50'
        }`}
      >
        🇸🇦 Bilingual{bilingualMode ? '' : ' OFF'}
      </button>

      
{/* Invoice Usage Widget */}
{(() => {
  const plan = authUser?.plan || 'basic';
const tier = authUser?.plan_tier || 'individual';
const posActive = moduleStatuses?.pos === 'active' || moduleStatuses?.pos === 'trial';
const isUnlimited = plan === 'ultra' || (plan === 'pro' && tier !== 'individual') || posActive;
if (isUnlimited) return null;

  const limit = plan === 'pro' ? 30 : tier === 'smb' ? 30 : tier === 'enterprise' ? 50 : 5;

  const [usage, setUsage] = React.useState<number | null>(null);
  React.useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt');
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    fetch(`${BASE}/invoices/monthly-usage`, {
      headers: { Authorization: `Bearer ${jwt}` },
    }).then(r => r.json()).then(d => setUsage(d.count || 0)).catch(() => {});
  }, []);

  if (usage === null) return null;
  const pct = Math.min(100, Math.round((usage / limit) * 100));
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, shrink: 0 }}>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {usage}/{limit} inv
      </div>
      <div style={{ width: 60, height: 5, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
})()}
     

      <div className="flex-1" />

      {/* Right actions */}
      <button
        onClick={() =>
  runHeaderAction(
    'share',
    {},
    handleGenerateShareLink,
  )
}
        className="h-[26px] px-2 text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-md flex items-center gap-1.5 transition-colors shrink-0"
      >
       <QrCode className="w-3 h-3" />
      </button>
   
<button
  onClick={() =>
  runHeaderAction(
    'open-scanner',
    {},
    onOpenScanner,
  )
}
  title="Scan QR"
  className="h-[30px] px-2.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center transition-colors shrink-0"
>
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m0 14v1M4 12h1m14 0h1M6.3 6.3l.7.7m10 10l.7.7M6.3 17.7l.7-.7m10-10l.7-.7"/>
    <circle cx="12" cy="12" r="3" strokeWidth="2"/>
  </svg>
</button>

      

      <button
  onClick={async () => {
    const plan = authUser?.plan || 'basic';
const tier = authUser?.plan_tier || 'individual';
const posActive = moduleStatuses?.pos === 'active' || moduleStatuses?.pos === 'trial';
const isUnlimited =
  plan === 'ultra' ||
  (plan === 'pro' && tier !== 'individual') ||
  posActive;
if (!isUnlimited) {
      const limit =
        plan === 'pro' ? 30 :
        tier === 'smb' ? 30 :
        tier === 'enterprise' ? 50 : 5;
      try {
        const jwt = localStorage.getItem('cloudairambo_jwt');
        const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
        const res = await fetch(`${BASE}/invoices/monthly-usage`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        const data = await res.json();
        const count = data.count || 0;
        if (count >= limit) {
          // fire popup via custom event — handled in App.tsx
          window.dispatchEvent(new CustomEvent('invoice-limit-reached', {
            detail: { count, limit, resetDate: data.reset_date, plan, tier }
          }));
          return;
        }
      } catch {}
    }
    runHeaderAction(
      'export-pdf',
      {},
      handleExportPDF,
    );
  }}
  title="Export PDF"
  className="h-[26px] px-2.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1.5 transition-colors shrink-0"
>
  <Download className="w-3.5 h-3.5" /> <span className="hidden xl:inline">Export PDF</span>
</button>
      <button
  onClick={() =>
  runHeaderAction(
    'save-template',
    {},
    onSaveAsTemplate,
  )
}
  className="h-[30px] px-3 text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-md flex items-center gap-1.5 transition-colors shrink-0"
  title="Save current layout as a reusable template"
>
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
  </svg>
  <span className="hidden xl:inline">Save as Templ</span>
</button>
<button
  onClick={() =>
  runHeaderAction(
    'reset-canvas',
    {},
    onResetCanvas,
  )
}
  className="h-[30px] px-3 text-xs font-medium bg-white border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 rounded-md flex items-center gap-1.5 transition-colors shrink-0"
  title="Reset canvas to fresh state"
>
  <span>🗑️</span>
  <span className="hidden xl:inline">Reset</span>
</button>

      {perms.canAccessGenerator && (
        <button
          onClick={() =>
  runHeaderAction(
    'save-invoice',
    {},
    handleSaveActiveWorkspaceAsBill,
  )
}
          className="h-[30px] px-3 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center gap-1.5 transition-colors shrink-0"
        >
          <CloudUpload className="w-3.5 h-3.5" /> 
        </button>
      )}


    </div>
{bilingualMode && (
  <div className="flex items-center gap-2 px-1 pb-1.5">
    <button
      onClick={() => {
  const direction =
    bilingualDirection === 'en-ar'
      ? 'ar-en'
      : 'en-ar';

  runHeaderAction(
    'set-bilingual-direction',
    { direction },
    () => setBilingualDirection(direction),
  );
}}
      className="h-[24px] px-2.5 text-[11px] font-medium bg-slate-800 text-white rounded-md flex items-center gap-1 border border-slate-700"
    >
      {bilingualDirection === 'en-ar' ? 'EN→AR' : 'AR→EN'}
    </button>
    <button
      onClick={() =>
  runHeaderAction(
    'restack-bilingual',
    {},
    handleRestackBilingual,
  )
}
      className="h-[24px] px-2.5 text-[11px] font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1 transition-colors"
    >
      ↕ Re-stack
    </button>

<button
  onClick={() =>
  runHeaderAction(
    'split-layout',
    {},
    handleSplitLayout,
  )
}
  className="h-[24px] px-2.5 text-[11px] font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-md flex items-center gap-1 transition-colors"
>
  ↔ Split
</button>
  </div>
)}
</header>
  );
};

export default GeneratorHeader;
